import WebAppAPI from '@/utils/WebAppAPI';

/* eslint-disable @typescript-eslint/no-explicit-any */

export type AttachmentParent =
  | { type: 'project'; orgId: string; projectId: string }
  | { type: 'task'; taskId: string }
  | { type: 'intake'; intakeId: string }
  | { type: 'task_comment'; taskId: string; commentId: string }
  | { type: 'intake_comment'; intakeId: string; commentId: string };

export type AttachmentStatus = 'pending' | 'ready';

export interface Attachment {
  _id: string;
  parent_type: string;
  parent_id: string;
  original_name: string;
  display_name?: string;
  mime_type: string;
  size: number;
  attachment_type?: string;
  status: AttachmentStatus;
  created_at: string;
  updated_at: string;
}

export interface UploadInitPayload {
  original_name: string;
  mime_type: string;
  size: number;
  display_name?: string;
}

export interface UploadInitResponse {
  attachment: Attachment;
  upload_url: string;
  fields: Record<string, string>;
  expires_at: string;
}

const uploadInitPath = (parent: AttachmentParent): string => {
  switch (parent.type) {
    case 'project':
      return `/organizations/${parent.orgId}/project/${parent.projectId}/attachments/upload`;
    case 'task':
      return `/tasks/${parent.taskId}/attachments/upload`;
    case 'intake':
      return `/intakes/${parent.intakeId}/attachments/upload`;
    case 'task_comment':
      return `/tasks/${parent.taskId}/task_comment/${parent.commentId}/attachments/upload`;
    case 'intake_comment':
      return `/intakes/${parent.intakeId}/intake_comment/${parent.commentId}/attachments/upload`;
  }
};

const listPath = (parent: AttachmentParent): string => {
  switch (parent.type) {
    case 'project':
      return `/organizations/${parent.orgId}/project/${parent.projectId}/attachments`;
    case 'task':
      return `/tasks/${parent.taskId}/attachments`;
    case 'intake':
      return `/intakes/${parent.intakeId}/attachments`;
    case 'task_comment':
      return `/tasks/${parent.taskId}/task_comment/${parent.commentId}/attachments`;
    case 'intake_comment':
      return `/intakes/${parent.intakeId}/intake_comment/${parent.commentId}/attachments`;
  }
};

const AttachmentService = {
  initUpload: async (
    parent: AttachmentParent,
    payload: UploadInitPayload
  ): Promise<UploadInitResponse> => {
    const { data } = await WebAppAPI().post(uploadInitPath(parent), payload);
    return data?.data ?? data;
  },

  /**
   * Uploads file bytes directly to S3 using the presigned POST data returned
   * by initUpload. No Crafter auth header is sent. Submits every returned
   * field exactly as-is, file last. Reports progress via onProgress (0-100).
   */
  uploadToS3: (
    uploadUrl: string,
    fields: Record<string, string>,
    file: File,
    onProgress?: (pct: number) => void,
    signal?: AbortSignal
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const form = new FormData();
      Object.entries(fields).forEach(([k, v]) => form.append(k, v));
      form.append('file', file);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', uploadUrl);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else
          reject(
            new Error(
              `S3 upload failed (${xhr.status}): ${xhr.responseText || xhr.statusText}`
            )
          );
      };
      xhr.onerror = () => reject(new Error('S3 upload network error'));
      xhr.onabort = () => reject(new DOMException('Aborted', 'AbortError'));

      if (signal) {
        if (signal.aborted) {
          xhr.abort();
          return;
        }
        signal.addEventListener('abort', () => xhr.abort());
      }

      xhr.send(form);
    });
  },

  completeUpload: async (attachmentId: string): Promise<Attachment> => {
    const { data } = await WebAppAPI().post('/attachments/complete-upload', {
      attachment_id: attachmentId,
    });
    return data?.data?.attachment ?? data?.data ?? data;
  },

  listAttachments: async (parent: AttachmentParent): Promise<Attachment[]> => {
    const { data } = await WebAppAPI().get(listPath(parent));
    const payload = data?.data ?? data;
    if (Array.isArray(payload)) return payload;
    return payload?.attachments ?? [];
  },

  deleteAttachment: async (attachmentId: string): Promise<void> => {
    await WebAppAPI().delete(`/attachments/${attachmentId}`);
  },

  /**
   * Fetches the file as a blob via the authenticated Crafter file route.
   * Crafter 302s to a short-lived signed CloudFront URL; axios follows the
   * redirect transparently. Returns a blob URL the caller is responsible
   * for revoking with URL.revokeObjectURL.
   */
  fetchFileBlobUrl: async (attachmentId: string): Promise<string> => {
    const response = await WebAppAPI().get(
      `/attachments/${attachmentId}/file`,
      { responseType: 'blob' }
    );
    return URL.createObjectURL(response.data);
  },

  /**
   * Opens an attachment in a new tab. Fetches the bytes via the authenticated
   * file route (since the bearer token can't ride along on a plain anchor
   * navigation), then opens a blob URL.
   */
  openAttachment: async (attachmentId: string): Promise<void> => {
    const blobUrl = await AttachmentService.fetchFileBlobUrl(attachmentId);
    window.open(blobUrl, '_blank', 'noopener,noreferrer');
    // Revoke after a delay so the new tab has time to load the resource.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  },

  /**
   * Runs init -> S3 -> complete for a single file against a parent. Throws
   * on any step failure. Caller can wrap in Promise.all with a concurrency
   * cap for bulk uploads.
   */
  uploadOne: async (
    parent: AttachmentParent,
    file: File
  ): Promise<Attachment> => {
    const init = await AttachmentService.initUpload(parent, {
      original_name: file.name,
      mime_type: file.type || 'application/octet-stream',
      size: file.size,
    });
    await AttachmentService.uploadToS3(init.upload_url, init.fields, file);
    return AttachmentService.completeUpload(init.attachment._id);
  },

  /**
   * Uploads a batch of files against a parent with a concurrency cap of 3.
   * Used by the comment composers (stage-then-upload after parent is created).
   * Resolves with per-file results so the caller can report partial failures.
   */
  uploadMany: async (
    parent: AttachmentParent,
    files: File[]
  ): Promise<Array<{ file: File; ok: true; attachment: Attachment } | { file: File; ok: false; error: string }>> => {
    const MAX = 3;
    const results: Array<
      | { file: File; ok: true; attachment: Attachment }
      | { file: File; ok: false; error: string }
    > = new Array(files.length);
    let cursor = 0;
    const workers = Array.from({ length: Math.min(MAX, files.length) }, async () => {
      while (true) {
        const idx = cursor++;
        if (idx >= files.length) return;
        const f = files[idx];
        try {
          const attachment = await AttachmentService.uploadOne(parent, f);
          results[idx] = { file: f, ok: true, attachment };
        } catch (err: any) {
          results[idx] = {
            file: f,
            ok: false,
            error: err?.response?.data?.message ?? err?.message ?? 'Upload failed',
          };
        }
      }
    });
    await Promise.all(workers);
    return results;
  },

  /**
   * Triggers a download of an attachment with its original filename.
   */
  downloadAttachment: async (
    attachmentId: string,
    filename: string
  ): Promise<void> => {
    const blobUrl = await AttachmentService.fetchFileBlobUrl(attachmentId);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  },
};

export default AttachmentService;
